import dayjs from 'dayjs';
import 'dayjs/locale/sk';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import React, {
  ReactNode,
  forwardRef,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UserSettingsContext } from '@eas/common-web';
import {
  CalendarSettings,
  defualtSettings,
  settingsId,
  settingsVersion,
} from '../settings/settings';
import { CalendarContext } from './calendar-context';
import { useStyles } from './calendar-styles';
import {
  CalendarDataSourceType,
  CalendarHandle,
  CalendarSettingsType,
} from './calendar-types';
import { Chart } from './chart';
import { Controls } from './controls';
import { getCurrentMonth } from './utils';

dayjs.extend(localizedFormat);
dayjs.extend(customParseFormat);
dayjs.locale('sk');

type CalendarProps = {
  children: ReactNode | ReactNode[];
  dataSource: CalendarDataSourceType;
};

export const Calendar = forwardRef<CalendarHandle, CalendarProps>(
  function Calendar({ children, dataSource }, ref) {
    const classes = useStyles();

    const wrapRef = useRef<HTMLDivElement>(null);

    const { getCustomSettings } = useContext(UserSettingsContext);

    // settings
    const savedSettings = getCustomSettings(
      settingsId,
      settingsVersion
    ) as CalendarSettings;

    const resolvedSettings: CalendarSettingsType = useMemo(() => {
      return {
        ...defualtSettings,
        ...savedSettings,
      };
    }, [savedSettings]);

    const initialCurrentDate = useMemo(() => {
      return getCurrentMonth();
    }, []);

    const [settings, setSettings] = useState<CalendarSettingsType>({
      ...resolvedSettings,
    });

    const [currentDate, setCurrentDate] = useState(initialCurrentDate);

    useImperativeHandle(
      ref,
      () => ({
        settings,
        setSettings,
        currentDate,
      }),
      [settings, currentDate]
    );

    return (
      <CalendarContext.Provider
        value={{
          wrapRef,
          dataSource,
          settings,
          setSettings,
          currentDate,
          setCurrentDate,
          classes,
        }}
      >
        <div className={classes.wrapper}>{children}</div>
      </CalendarContext.Provider>
    );
  }
) as unknown as React.FC<
  CalendarProps & {
    ref?: React.Ref<CalendarHandle>;
  }
> & {
  Chart: typeof Chart;
  Controls: typeof Controls;
  useStyles: typeof useStyles;
};

Calendar.Chart = Chart;
Calendar.Controls = Controls;
Calendar.useStyles = useStyles;
